import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Pencil } from "lucide-react";
import { useState } from "react";
import { ItemComments } from "@/components/items/ItemComments";
import { ItemEditForm } from "@/components/items/ItemEditForm";
import { ErrorState } from "@/components/shared/error-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useItem, useUpdateItem } from "@/hooks/use-items";
import { formatDate } from "@/lib/format";

export function ItemDetailPage() {
  const { itemId } = useParams({ strict: false }) as { itemId: string };
  const { data: item, isLoading, isError, error, refetch } = useItem(itemId);
  const updateItem = useUpdateItem(itemId);
  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load item"} onRetry={() => refetch()} />
    );
  }

  if (!item) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Item not found.</p>
        <Link to="/dashboard/items" className="text-sm text-primary hover:underline mt-2 block">
          Back to items
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/dashboard/items"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Items
        </Link>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-2xl font-bold tracking-tight">{item.name}</h1>
        {!editing && <StatusBadge status={item.status} />}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Details</CardTitle>
          {!editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <ItemEditForm
              item={item}
              onSave={(values) =>
                updateItem.mutate(
                  {
                    name: values.name,
                    description: values.description ?? null,
                    status: values.status,
                  },
                  { onSuccess: () => setEditing(false) },
                )
              }
              onCancel={() => setEditing(false)}
              isSaving={updateItem.isPending}
            />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <div className="mt-1">
                    <StatusBadge status={item.status} />
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Created</span>
                  <p className="mt-1">{formatDate(item.createdAt)}</p>
                </div>
              </div>
              {item.description && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Description</span>
                  <p className="mt-1">{item.description}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <ItemComments itemId={item.id} />
        </CardContent>
      </Card>
    </div>
  );
}
